import { cos } from "@suse-o11y/trigonometry";
import { useLatest } from "@suse-o11y/use-latest";
import Konva from "konva";
import React from "react";

import { AssignNodeField } from "./_AssignNodeField";
import { Node, type NodeConfig } from "./Node";

export type ShapeAnimation = "pulse";

export interface ShapeConfig extends NodeConfig, Pick<Konva.ShapeConfig, "strokeWidth" | "fill" | "dash"> {
	stroke?: string;
	animate?: "pulse";
}

export interface ShapeProps extends ShapeConfig {
	instance: Konva.Shape;
}

export const Shape: React.FC<React.PropsWithChildren<ShapeProps>> = ({
	instance,
	stroke,
	strokeWidth,
	fill,
	dash,
	animate,
	children,
	...config
}) => (
	<Node instance={instance} {...config}>
		{children}
		{animate === "pulse" && <RunPulseAnimation instance={instance} opacity={instance.opacity()} />}
		{stroke != null && <AssignNodeField instance={instance} method="stroke" value={stroke} />}
		{strokeWidth != null && <AssignNodeField instance={instance} method="strokeWidth" value={strokeWidth} />}
		{fill != null && <AssignNodeField instance={instance} method="fill" value={fill} />}
		{dash != null && <AssignNodeField instance={instance} method="dash" serialize value={dash} />}
	</Node>
);

const RunPulseAnimation: React.FC<{
	instance: Konva.Shape;
	opacity: undefined | number;
}> = ({ instance, opacity }) => {
	const latestOpacity = useLatest(opacity);

	React.useEffect(() => {
		// the Konva frame time starts at the beginning of the animation
		// add milliseconds since epoch to make the animation independent of (partial) updates
		const now = Date.now();
		const animation = new Konva.Animation((frame) => {
			if (frame) {
				instance.opacity(0.25 + (0.75 * (1.0 + cos((now + frame.time) / 250))) / 2.0);
			}
		});

		animation.start();

		return () => {
			animation.stop();
			// reset the animated opacity to the latest props' value
			instance.opacity(latestOpacity()!);
		};
	}, [instance, latestOpacity]);

	return null;
};
